import { useOktaAuth } from "@okta/okta-react";
import useSWR from "swr";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useCallback, useEffect, useState } from "react";
import { isEuEnabledCached } from "../../feature-flags/euFeatureFlag";
import { isDirectRegionConfigured, LOGIN_HINT_KEY, SILENT_PROBE_DONE_KEY } from "../../lib/region-auth";

let hasHandledIssuerMismatch = false;

export const useSession = () => {
    const { oktaAuth, authState } = useOktaAuth();

    const getAccessToken = useCallback(() => oktaAuth.getOrRenewAccessToken(), [oktaAuth]);

    const [, setTokenVersion] = useState(0);

    useEffect(() => {
        const handler = (key: string) => {
            if (key !== "accessToken") return;
            setTokenVersion((v) => v + 1);
        };
        oktaAuth.tokenManager.on("added", handler);
        return () => oktaAuth.tokenManager.off("added", handler);
    }, [oktaAuth]);

    const accessToken = oktaAuth.getAccessToken();

    const { data: user, error: oktaUserError } = useSWR(
        authState?.isAuthenticated ? "okta-user-session" : null,
        async () => {
            await getAccessToken();
            return oktaAuth.getUser();
        },
        { revalidateOnFocus: true, revalidateOnReconnect: true, refreshInterval: 300000 }
    );

    const signIn = useCallback(async () => {
        oktaAuth.setOriginalUri(toRelativeUrl(window.location.href, window.location.origin));
        if (!isEuEnabledCached() || !isDirectRegionConfigured()) {
            await oktaAuth.signInWithRedirect();
            return;
        }
        // Direct-region cross-app SSO. We attempt a SILENT probe (prompt=none) exactly
        // ONCE per login attempt: if the region Okta already holds a session (e.g. from
        // the Home login) it returns a code with no UI. If there is NO active session
        // Okta returns ?error=login_required to /callback and the CallbackErrorHandler
        // sends the user to the broker.
        //
        // Loop guard: the probe marker is set BEFORE the redirect and is only ever
        // cleared once a token is obtained or a terminal callback error ends the flow.
        // It deliberately SURVIVES the broker fallback (see handleCallbackError), so
        // while it is set we never probe again and any further signIn is a real
        // interactive login at the region.
        //
        // login_hint comes from the URL, or from the value stashed by the LoginRegion
        // step (which survives router navigations that drop the query string).
        const loginHint =
            new URLSearchParams(window.location.search).get("login_hint") ??
            window.sessionStorage.getItem(LOGIN_HINT_KEY) ??
            undefined;
        const probed = window.sessionStorage.getItem(SILENT_PROBE_DONE_KEY) === "1";
        if (probed) {
            // Interactive login at the region (user came via broker on purpose, or the
            // silent probe already ran). Forward login_hint to prefill.
            await oktaAuth.signInWithRedirect(loginHint ? { loginHint } : undefined);
            return;
        }
        window.sessionStorage.setItem(SILENT_PROBE_DONE_KEY, "1");
        await oktaAuth.signInWithRedirect({
            extraParams: { prompt: "none" },
            ...(loginHint ? { loginHint } : {})
        });
    }, [oktaAuth]);

    const signOut = useCallback(async () => {
        try {
            await oktaAuth.signOut();
        } finally {
            oktaAuth.tokenManager.clear();
        }
    }, [oktaAuth]);

    const oktaClientIssuer = oktaAuth.getIssuerOrigin();
    const accessTokenIssuer = oktaAuth.authStateManager.getAuthState()?.accessToken?.claims.iss;

    const hasIssuerMismatch =
        authState?.isAuthenticated && !!accessTokenIssuer && !accessTokenIssuer.includes(oktaClientIssuer);

    const isLoading = !authState || (authState?.isAuthenticated && !oktaUserError && !user) || hasIssuerMismatch;

    useEffect(() => {
        if (hasIssuerMismatch && !hasHandledIssuerMismatch) {
            hasHandledIssuerMismatch = true;
            oktaAuth.tokenManager.clear();
            signIn().catch(() => {
                hasHandledIssuerMismatch = false;
            });
        }
    }, [hasIssuerMismatch, oktaAuth, signIn]);

    return {
        user,
        oktaAuth,
        isAuthenticated: authState?.isAuthenticated,
        accessToken,
        getAccessToken,
        signIn,
        signOut,
        error: oktaUserError ?? null,
        isLoading
    };
};
